import { AUSTRALIA_ISO } from '@/constants/general-words.const';
import { QuoteVehicleUseTypes } from '@/enums/quote-vehicle-use-types.enum';
import {
  Address,
  Coverage,
  InsurancePolicy,
  InsuranceQuoteType,
  IQuote,
  QuoteTypeCodes,
  RACQAddressResult,
} from '@/interfaces';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { differenceInYears, isAfter } from 'date-fns';
import { stringify } from 'querystring';

export const minDate = new Date('0001-01-01T00:00:00Z');

export async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function calculateAge(birthDate: Date): number {
  const today = new Date();

  // Validate that the birthDate is not in the future
  if (isAfter(birthDate, today)) {
    throw new Error('Birth date cannot be in the future');
  }

  return differenceInYears(today, birthDate);
}

export function findFieldContent(fields: any[], fieldName: string): string {
  const field = fields.find((item: any) => item.name === fieldName);
  return field?.content?.value || '';
}

export function sanitizeHTML(value: string) {
  return value.replace(/[^\w. ]/gi, function (c) {
    return '&#' + c.charCodeAt(0) + ';';
  });
}

export function htmlEncode(str: string) {
  return String(str).replace(/[^\w. ]/gi, function (c) {
    return '&#' + c.charCodeAt(0) + ';';
  });
}

const unSafeValues = [
  { character: '&', encoded: '\\u0026' },
  { character: '<', encoded: '\\u003C' },
  { character: '>', encoded: '\\u003E' },
  { character: '%', encoded: '\\u0025' },
];

export function jsEncode(str: string) {
  unSafeValues.forEach((unsafeValue) => {
    str = str.replaceAll(unsafeValue.character, unsafeValue.encoded);
  });

  return str;
}

export const jsDecode = (str: string) => {
  unSafeValues.forEach((unsafeValue) => {
    str = str.replaceAll(unsafeValue.encoded, unsafeValue.character);
  });
  return str;
};

export const urlRedirect = (url: string, queryParams?: Record<string, string>) => {
  if (!url || typeof url != 'string') return;
  let query = '';
  if (queryParams) {
    query = (url.includes('?') ? '&' : '?') + stringify(queryParams);
  }
  window.location.href = encodeURI(url + query);
};

export const isVehicleUseAsBusinessRatedAsPrivate = (quote: IQuote, typeOfBusinessRaw: INameValue[]) => {
  if (!quote) return false;

  return typeOfBusinessRaw.some(
    (type: any) =>
      type.value?.toLowerCase() === quote.motorQuote?.vehicleDetails?.businessUseType?.value?.toLowerCase() &&
      type.businessRatedAsPrivate
  );
};

export const isShowBusinessUseWarning = (quote: IQuote, isBusinessUseWarningEnabled: boolean) => {
  if (!quote) return false;

  return (
    isBusinessUseWarningEnabled &&
    quote.quoteType == InsuranceQuoteType.MotorComprehensive &&
    quote.motorQuote?.vehicleDetails?.vehicleUseType == QuoteVehicleUseTypes.Business
  );
};

export const useUniqueId = (prefix = 'uid') => {
  const id = `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
  return id;
};

export const hasSameProperty = (items: any[], prop: string) => {
  if (items.length === 0) return true;

  const firstValue = items[0][prop];
  return items.every((item) => item[prop] === firstValue);
};

export const showCombined = (coverages: Coverage[]) => {
  return coverages.length > 1 && !hasSameProperty(coverages, 'coverageCode');
};

/**
 * Apply logic to determine of what coverage type should be used for the display icon.
 * If shouldShowCombined is means it's mixed coverage types.
 * @param insuranceProduct
 * @returns
 */
export const getInsuranceCoverageTypeToUseForIcon = (insuranceProduct: InsurancePolicy) => {
  const shouldShowCombined = showCombined(insuranceProduct?.coverages ?? []);
  const firstCoverage = insuranceProduct.coverages[0];
  const useCoverageType =
    firstCoverage.coverageCode?.toLowerCase() === QuoteTypeCodes[InsuranceQuoteType.Caravan]?.toLowerCase() &&
    shouldShowCombined
      ? QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]
      : firstCoverage.coverageCode;
  return useCoverageType;
};

export const getLast100Years = (): { label: string; value: string }[] => {
  const currentYear = new Date().getFullYear();
  const years: { label: string; value: string }[] = [];

  for (let i = 0; i < 100; i++) {
    const year = (currentYear - i).toString();
    years.push({ label: year, value: year });
  }

  return years;
};

/**
 * Handles showing international address in self service for read only mode until other address related fixes are done.
 * https://wiki.racqgroup.local/confluence/spaces/WS/pages/373739427/International+address+issues
 * @param address
 * @param country
 * @returns
 */
export const buildInternationalAddressLine = (
  address: Partial<RACQAddressResult>,
  country: string,
  limitLength: boolean = true
) => {
  let addressLine = joinAddressLine(address);

  const commaAfterSuburb = address?.postcode?.trim().length ? ',' : '';
  addressLine +=
    (address.suburb?.trim()?.length ?? 0) > 0 ? `, ${address?.suburb?.trim()?.toUpperCase()}${commaAfterSuburb} ` : '';
  if ((address.postcode?.trim().length ?? 0) > 0) {
    addressLine += (address.postcode?.trim().length ?? 0) > 0 ? `${address?.postcode?.trim()}` : '';
  }

  addressLine = addressLine.trim();
  addressLine += (address.country?.trim()?.length ?? 0) > 0 ? `, ${country?.trim()?.toUpperCase()}` : '';

  const addressLength = 41;
  if (addressLine.length > addressLength && limitLength) {
    addressLine = `${addressLine.substring(0, addressLength)}...`;
  }

  return addressLine;
};

/**
 * Joining street number + street name
 * @param addressLine
 */
export const joinAddressLine = (address: Partial<RACQAddressResult>) => {
  let addressLine = address.unitNumber?.trim() ? `${address.unitNumber.trim()} ` : '';
  addressLine += address.buildingLevel?.trim() ? `${address.buildingLevel.trim()} ` : '';
  addressLine += address.streetNumber?.trim() ? `${address.streetNumber.trim()} ` : '';
  addressLine += address.street?.trim() ? `${address.street.trim()}` : '';
  return addressLine;
};

export const joinIdentityAddressLine = (address: Partial<Address>) => {
  let addressLine = address.unit?.trim() ? `${address.unit.trim()} ` : '';
  addressLine += address.buildingLevel?.trim() ? `${address.buildingLevel.trim()} ` : '';
  addressLine += address.streetNumber?.trim() ? `${address.streetNumber.trim()} ` : '';
  addressLine += address.street?.trim() ? `${address.street.trim()}` : '';
  return addressLine;
};

export const buildIdentityAddress = (address: Partial<Address>) => {
  if (address.country !== AUSTRALIA_ISO) {
    if (address.state?.length ?? 0 > 0) {
      return `${joinIdentityAddressLine(address)}, ${address.suburb} ${address.state} ${address.postcode} ${address.country}`;
    } else {
      return `${joinIdentityAddressLine(address)}, ${address.suburb} ${address.postcode} ${address.country}`;
    }
  } else {
    return `${joinIdentityAddressLine(address)}, ${address.suburb} ${address.state} ${address.postcode}`;
  }
};
/**
 * if the value is null
 * @param val
 * @returns
 */
export const isEmpty = (val: unknown): boolean => {
  if (val == null) return true;
  if (typeof val === 'string' && val.trim() === '') return true;
  if (Array.isArray(val) && val.length === 0) return true;
  return false;
};

/**
 * format 32 character string as UUID
 * @param id
 * @returns
 */
export const toUUID = (id: string) =>
  id?.length === 32
    ? `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`
    : id;

/**
 * Mask email
 * @param email
 * @returns return masked email
 */
export const maskEmail = (email: string): string => {
  return email.replaceAll(/\.(?=[^@]*@)/g, '*');
};

/**
 * Detect iOS Safari (including iPadOS).
 */
export const isIosSafari = (): boolean => {
  const ua = navigator.userAgent.toLocaleLowerCase();
  const ios = /iphone|ipad|ipod/.test(ua);
  const safari = /^((?!chrome|android).)*safari/i.test(ua);
  return ios && safari;
};

/**
 * update query string parameter in the url
 * @param url url
 * @param key name of query string
 * @param value value
 * @returns
 */
export const updateQueryStringParameter = (url: string, key: string, value: string): string => {
  const urlObject = new URL(url);
  urlObject.searchParams.set(key, value);
  return urlObject.toString();
};
